import { ReactNode, useContext, useRef, Key, useCallback } from 'react';
import { TreeState } from 'react-stately';
import { mergeProps, useFocusVisible, useHover, useMenuItem } from 'react-aria';
import { isMobile } from '@direct-frontend/platform-code';

import { cnListBoxItemSeparator, isBorderElement, useItemClasses } from '../../ListBoxBase/utils';
import { MenuItem, MenuItemOption, MenuItemSection, MenuSingleItem } from '../types';
import { TestProps } from '../../../internal/types/testTypes';
import { Separator } from '../../ListBoxBase/Separator/Separator';
import { useItemTooltip } from '../../../hooks/useItemTooltip';
import { getMenuItemKey } from '../utils';
import { MenuContext } from '../context';

interface MenuOptionProps<ValueType extends string, SectionType extends string> extends TestProps {
    item: MenuItemOption<ValueType>;
    state: TreeState<MenuItem<ValueType, SectionType>>;
    children: ReactNode;
}

export const MenuOption = <ValueType extends string, SectionType extends string>(
    props: MenuOptionProps<ValueType, SectionType>,
) => {
    const { item, state, children } = props;
    const { addonRight, onAction, value } = item;
    const hasAddonRight = Boolean(addonRight);
    const ref = useRef<HTMLDivElement>(null);
    const { isFirst, isLast } = isBorderElement<
        ValueType,
        SectionType,
        MenuSingleItem<ValueType>,
        MenuItemSection<ValueType, SectionType>
    >(item, state.collection, getMenuItemKey);
    const { shouldCloseOnSelect } = useContext(MenuContext);

    const onActionOptions = useCallback(
        (key: Key) => {
            onAction?.(key);
        },
        [onAction],
    );

    const { menuItemProps, isDisabled, isFocused } = useMenuItem(
        {
            key: value,
            onAction: onAction ? onActionOptions : undefined,
            closeOnSelect: shouldCloseOnSelect?.(value),
        },
        state,
        ref,
    );

    const {
        onPointerEnter: _onPointerEnter,
        onPointerLeave: _onPointerLeave,
        onTouchStart: _onTouchStart,
        onMouseEnter: _onMouseEnter,
        onMouseLeave: _onMouseLeave,
        ...menuItemPropsWithoutHover
    } = menuItemProps;

    const { isFocusVisible } = useFocusVisible();

    const { isHovered, hoverProps } = useHover({ isDisabled });
    const { className } = useItemClasses({
        isFocused: (isFocusVisible && isFocused) || isHovered,
        isDisabled,
        hasAddonRight,
        isFirst,
        isLast,
    });

    const withSeparator = isMobile() && !isLast;
    const ItemTooltip = item.tooltip;

    const {
        triggerProps: tooltipTriggerProps,
        tooltipProps,
        descriptionNode,
    } = useItemTooltip({
        triggerRef: ref,
        hasTooltip: Boolean(ItemTooltip),
        isDisabled,
        description: item.tooltipDescription,
    });

    return (
        <>
            <div
                ref={ref}
                className={className}
                data-testid={props['data-testid']}
                {...mergeProps(menuItemPropsWithoutHover, tooltipTriggerProps, hoverProps)}
            >
                {children}
            </div>
            {ItemTooltip && <ItemTooltip triggerRef={ref} {...tooltipProps} />}
            {descriptionNode}
            {withSeparator && <Separator className={cnListBoxItemSeparator(null)} />}
        </>
    );
};
